
export type PayrollVoucherErrorKind =
  | "version-conflict"
  | "already-exported"
  | "validation"
  | "forbidden"
  | "unknown";

export interface ParsedPayrollVoucherError {
  kind: PayrollVoucherErrorKind;
  title: string;
  description: string;
}

interface RawErrorPayload {
  statusCode?: number;
  messages?: string[];
  message?: string | string[];
}

interface RtkQueryError {
  status?: number | string;
  data?: RawErrorPayload | string;
  message?: string;
}

const HTTP_CONFLICT: number = 409;
const HTTP_UNPROCESSABLE: number = 422;
const HTTP_BAD_REQUEST: number = 400;
const HTTP_FORBIDDEN: number = 403;

const TITLE_BY_KIND: Record<PayrollVoucherErrorKind, string> = {
  "version-conflict": "Vale já decidido",
  "already-exported": "Período já fechado",
  validation: "Decisão inválida",
  forbidden: "Sem permissão",
  unknown: "Erro ao confirmar a decisão",
};

const FALLBACK_DESCRIPTION_BY_KIND: Record<PayrollVoucherErrorKind, string> = {
  "version-conflict":
    "Outro operador do RH decidiu este vale enquanto você o analisava. A fila foi atualizada — confira a decisão registrada antes de decidir de novo.",
  "already-exported":
    "Este vale já foi enviado ao Sênior e o período está fechado. Apenas reversão é permitida.",
  validation:
    "Verifique o parcelamento: a soma das parcelas precisa fechar exatamente com o valor do vale.",
  forbidden: "Você não tem permissão para decidir vales.",
  unknown: "Não foi possível confirmar a decisão. Tente novamente.",
};

const KIND_BY_STATUS_CODE: Record<number, PayrollVoucherErrorKind> = {
  [HTTP_CONFLICT]: "version-conflict",
  [HTTP_UNPROCESSABLE]: "already-exported",
  [HTTP_BAD_REQUEST]: "validation",
  [HTTP_FORBIDDEN]: "forbidden",
};

const extractPayload = (error: unknown): RawErrorPayload => {
  const rtkError = error as RtkQueryError | undefined;
  if (!rtkError) return {};

  if (typeof rtkError.data === "string") {
    return {
      message: rtkError.data,
      statusCode: Number(rtkError.status) || undefined,
    };
  }

  return {
    statusCode: Number(rtkError.status) || rtkError.data?.statusCode,
    ...(rtkError.data ?? {}),
  };
};

const flattenMessage = (payload: RawErrorPayload): string => {
  const fromPlural: string | undefined = payload.messages?.[0];
  if (fromPlural) return fromPlural;

  if (Array.isArray(payload.message)) return payload.message.join(", ");
  return payload.message ?? "";
};

export const parsePayrollVoucherError = (
  error: unknown,
): ParsedPayrollVoucherError => {
  const payload: RawErrorPayload = extractPayload(error);
  const kind: PayrollVoucherErrorKind =
    KIND_BY_STATUS_CODE[payload.statusCode ?? 0] ?? "unknown";
  const fromApi: string = flattenMessage(payload);

  return {
    kind,
    title: TITLE_BY_KIND[kind],
    description: fromApi || FALLBACK_DESCRIPTION_BY_KIND[kind],
  };
};
